"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin, Phone, Mail, Navigation, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function HerFinnerDuOss() {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const [shouldLoadMap, setShouldLoadMap] = useState(false);
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      setShouldLoadMap(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const [entry] = entries;
      if (entry && entry.isIntersecting) {
        setShouldLoadMap(true);
        observer.disconnect();
      }
    }, {
      rootMargin: "300px",
      threshold: 0
    });
    if (mapContainerRef.current) {
      observer.observe(mapContainerRef.current);
    }
    return () => {
      observer.disconnect();
    };
  }, []);
  const address = "Fryksdalsvägen 26, 671 42 Arvika";
  const phone = "+46 (0) 570-808 03";
  const email = "info@proffsbadrum.se";
  const hours = "Måndag-Fredag: 06:45-16:30";
  const serviceAreas = [{
    name: "Arvika",
    path: "/badrumsrenovering-arvika"
  }, {
    name: "Karlstad",
    path: "/badrumsrenovering-karlstad"
  }, {
    name: "Sunne",
    path: "/badrumsrenovering-sunne"
  }, {
    name: "Säffle",
    path: "/badrumsrenovering-saffle"
  }, {
    name: "Charlottenberg",
    path: "/badrumsrenovering-charlottenberg"
  }, {
    name: "Värmland",
    path: "/badrumsrenovering-varmland"
  }];
  return <section data-section-id="100043" id="hitta-till-oss" className="relative w-full h-[800px] md:h-[700px] bg-secondary overflow-hidden">
      {}
      <div ref={mapContainerRef} className="absolute inset-0 w-full h-full bg-background">
        {shouldLoadMap ? <iframe title="Karta över vårt showroom" width="100%" height="100%" frameBorder="0" scrolling="no" marginHeight={0} marginWidth={0} src="https://www.openstreetmap.org/export/embed.html?bbox=12.55,59.86,12.62,59.90&layer=mapnik&marker=12.585,59.88" className="w-full h-full object-cover grayscale contrast-100 opacity-60 mix-blend-multiply dark:mix-blend-screen dark:opacity-40 dark:invert" /> : <div className="w-full h-full grayscale contrast-100 opacity-60 mix-blend-multiply dark:mix-blend-screen dark:opacity-40 dark:invert" />}
      </div>

      {}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-t md:bg-gradient-to-r from-background/90 via-background/30 to-transparent" />

      {}
      <div className="absolute z-10 bottom-0 left-0 right-0 p-4 md:p-0 md:bottom-auto md:top-1/2 md:-translate-y-1/2 md:left-12 lg:left-24 w-full md:w-[420px]">
        <div className="bg-background/90 backdrop-blur-xl border border-border shadow-2xl rounded-xl p-6 md:p-8 pb-8 md:pb-10 flex flex-col gap-8">
          
          {}
          <div>
            <h2 className="text-3xl font-default font-semibold text-foreground tracking-tight mb-2">
              Hitta till oss
            </h2>
            <p className="text-muted-foreground font-default text-sm leading-relaxed">Välkommen till vår virtuella utställningshall. Utforska våra gallerier online och kontakta oss för att boka ett kostnadsfritt hembesök på din fastighet.</p>
          </div>

          {}
          <div className="flex flex-col gap-5">
            <div className="flex items-start gap-4 group">
              <div className="bg-primary/10 p-2.5 rounded-lg text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <MapPin className="w-5 h-5" />
              </div>
              <div className="pt-1">
                <p className="font-default font-semibold text-foreground text-sm">Huvudkontor</p>
                <p className="text-muted-foreground font-default text-sm mt-0.5">{address}</p>
              </div>
            </div>

            <div className="flex items-start gap-4 group">
              <div className="bg-primary/10 p-2.5 rounded-lg text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <Clock className="w-5 h-5" />
              </div>
              <div className="pt-1">
                <p className="font-default font-semibold text-foreground text-sm">Öppettider</p>
                <p className="text-muted-foreground font-default text-sm mt-0.5">{hours}</p>
              </div>
            </div>

            <div className="flex items-start gap-4 group">
              <div className="bg-primary/10 p-2.5 rounded-lg text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <Phone className="w-5 h-5" />
              </div>
              <div className="pt-1">
                <p className="font-default font-semibold text-foreground text-sm">Telefon</p>
                <Link to={`tel:${phone.replace(/\s/g, '')}`} className="text-muted-foreground font-default text-sm mt-0.5 hover:text-primary transition-colors">
                  {phone}
                </Link>
              </div>
            </div>

            <div className="flex items-start gap-4 group">
              <div className="bg-primary/10 p-2.5 rounded-lg text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <Mail className="w-5 h-5" />
              </div>
              <div className="pt-1">
                <p className="font-default font-semibold text-foreground text-sm">E-post</p>
                <Link to={`mailto:${email}`} className="text-muted-foreground font-default text-sm mt-0.5 hover:text-primary transition-colors">
                  {email}
                </Link>
              </div>
            </div>
          </div>

          {}
          <div className="pt-2 border-t border-border/50">
            <h3 className="font-default font-semibold text-foreground mb-3 text-xs uppercase tracking-widest text-muted-foreground">
              VÅRA VERKSAMHETSOMRÅDEN
            </h3>
            <ul className="flex flex-wrap gap-2">
              {serviceAreas.map((area, i) => <Link key={i} data-index={i} to={area.path} className="text-xs font-default font-medium bg-secondary text-secondary-foreground px-3 py-1.5 rounded-md border border-border/60 hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer">
                  {area.name}
                </Link>)}
            </ul>
          </div>

          {}
          <Button asChild size="lg" className="w-full group mt-2 shadow-md bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to={`https://maps.google.com/?q=${encodeURIComponent(address)}`} newTab>
              <Navigation className="w-4 h-4 mr-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              Få vägbeskrivning
            </Link>
          </Button>

        </div>
      </div>
    </section>;
}